import { apiClient } from './client';
import type {
  Schema,
  SchemaListItem,
  CreateSchemaRequest,
  UpdateSchemaRequest,
  CreateSchemaVersionRequest,
  SchemaInferenceResult,
} from '@/features/schemas/types/schema.types';

/**
 * Schema API endpoints
 *
 * Provides methods for managing schemas in the data lakehouse:
 * - List, get, create, update, delete schemas
 * - Create new schema versions
 * - Infer schema from uploaded files
 */
export const schemaEndpoints = {
  /**
   * List all schemas with optional pagination and search
   *
   * @param params - Optional query parameters
   * @param params.page - Page number (1-indexed)
   * @param params.size - Number of items per page (backend uses 'size' parameter)
   * @param params.search - Search query for filtering schemas by name
   * @returns Promise resolving to paginated schema list
   *
   * @example
   * ```ts
   * const response = await schemaEndpoints.list({ page: 1, size: 20, search: 'user' });
   * console.log(response.data.items); // SchemaListItem[]
   * ```
   */
  list: (params?: { page?: number; size?: number; search?: string }) =>
    apiClient.get<{
      items: SchemaListItem[];
      total: number;
      page: number;
      limit: number;
      pages: number;
    }>('/schemas', { params }),

  /**
   * Get detailed information for a specific schema
   *
   * @param id - Schema UUID
   * @returns Promise resolving to full schema details
   *
   * @example
   * ```ts
   * const response = await schemaEndpoints.get('550e8400-e29b-41d4-a716-446655440000');
   * console.log(response.data.fields); // SchemaField[]
   * ```
   */
  get: (id: string) => apiClient.get<Schema>(`/schemas/${id}`),

  /**
   * Create a new schema
   *
   * @param data - Schema creation payload
   * @param data.name - Unique schema name
   * @param data.compatibility_mode - Schema evolution compatibility mode
   * @param data.fields - Array of schema field definitions
   * @param data.description - Optional description
   * @returns Promise resolving to created schema
   *
   * @example
   * ```ts
   * const response = await schemaEndpoints.create({
   *   name: 'user_events',
   *   compatibility_mode: 'BACKWARD',
   *   fields: [
   *     { name: 'user_id', type: 'string', nullable: false },
   *     { name: 'timestamp', type: 'timestamp', nullable: false }
   *   ],
   *   description: 'User activity events'
   * });
   * ```
   */
  create: (data: CreateSchemaRequest) => {
    // Transform frontend field names to backend field names
    const transformedData = {
      ...data,
      fields: data.fields.map(field => {
        const { isPII, ...rest } = field;
        return {
          ...rest,
          pii: isPII ?? false, // Map isPII to pii
        };
      }),
    };
    console.log('Schema create request payload:', JSON.stringify(transformedData, null, 2));
    return apiClient.post<Schema>('/schemas', transformedData);
  },

  /**
   * Update an existing schema
   *
   * @param id - Schema UUID
   * @param data - Partial schema update payload
   * @returns Promise resolving to updated schema
   *
   * @example
   * ```ts
   * const response = await schemaEndpoints.update('550e8400-e29b-41d4-a716-446655440000', {
   *   description: 'Updated description',
   *   compatibility_mode: 'FULL'
   * });
   * ```
   */
  update: (id: string, data: UpdateSchemaRequest) =>
    apiClient.put<Schema>(`/schemas/${id}`, data),

  /**
   * Delete a schema
   *
   * WARNING: This will fail if the schema is referenced by any datasets.
   * Remove dataset associations first before deletion.
   *
   * @param id - Schema UUID
   * @returns Promise resolving when deletion is complete
   *
   * @example
   * ```ts
   * await schemaEndpoints.delete('550e8400-e29b-41d4-a716-446655440000');
   * ```
   */
  delete: (id: string) => apiClient.delete(`/schemas/${id}`),

  /**
   * Create a new version of an existing schema
   *
   * The new version must be compatible with previous versions according to
   * the schema's compatibility mode setting.
   *
   * @param id - Schema UUID
   * @param data - New version payload with updated fields
   * @returns Promise resolving to updated schema with new version
   *
   * @example
   * ```ts
   * const response = await schemaEndpoints.createVersion('550e8400-e29b-41d4-a716-446655440000', {
   *   fields: [
   *     { name: 'user_id', type: 'string', nullable: false },
   *     { name: 'timestamp', type: 'timestamp', nullable: false },
   *     { name: 'event_type', type: 'string', nullable: true } // New field
   *   ]
   * });
   * ```
   */
  createVersion: (id: string, data: CreateSchemaVersionRequest) =>
    apiClient.post<Schema>(`/schemas/${id}/versions`, data),

  /**
   * Infer schema from an uploaded file
   *
   * Automatically detects field types, nullable constraints, and potential PII
   * from sample data. Supports Parquet, CSV, JSON formats.
   *
   * @param file - File to analyze (Parquet, CSV, or JSON)
   * @returns Promise resolving to inferred schema structure
   *
   * @example
   * ```ts
   * const file = new File([blob], 'data.parquet', { type: 'application/octet-stream' });
   * const response = await schemaEndpoints.inferFromFile(file);
   * console.log(response.data.fields); // Inferred SchemaField[]
   * console.log(response.data.detected_pii); // ['email', 'phone_number']
   * ```
   */
  inferFromFile: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    return apiClient.post<SchemaInferenceResult>('/schemas/infer', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 60000, // 60 seconds for file processing
    });
  },
};
